import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import { message } from "antd";
import pLimit from "p-limit";
import {
  completeOpenResourceFileUpload,
  initOpenResourceFileUpload,
  openResourceFileUploadProgress,
} from "@/apis/discover";
import { calculateHashes } from "@/utils/calculateHash";
import { chunkSize } from "@/utils/calculateChunkSize";

const UploadQueueContext = createContext(null);
const partLimit = pLimit(3);
const fileLimit = pLimit(3);
const SMALL_FILE_THRESHOLD = 100 * 1024 * 1024;
const MULTIPART_MIN_CHUNK_SIZE = 5 * 1024 * 1024;
const RESOURCE_FILE_TYPE = "CommunityResources";

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const getContentType = (file) => file.type || "application/octet-stream";

const getUploadRelativePath = (task) => task.fileRelativePath || task.fileName;

const getTaskId = (resourceUuid, uid) => `${resourceUuid || "resource"}-${uid}`;

const isStoppedStatus = (status) => ["paused", "canceled"].includes(status);

const isCanceledError = (err) =>
  err?.code === "ERR_CANCELED" || err?.name === "CanceledError";

const putWithRetry = async (url, data, options, retryCount = 2) => {
  let lastError;
  for (let attempt = 0; attempt <= retryCount; attempt += 1) {
    try {
      return await axios.put(url, data, options);
    } catch (err) {
      if (isCanceledError(err)) {
        throw err;
      }
      lastError = err;
      if (attempt < retryCount) {
        await sleep(800 * (attempt + 1));
      }
    }
  }
  throw lastError;
};

export const UploadQueueProvider = ({ children }) => {
  const [tasks, setTasks] = useState([]);
  const taskMapRef = useRef(new Map());
  const abortControllerMapRef = useRef(new Map());

  const patchTask = useCallback((id, patch) => {
    taskMapRef.current.set(id, {
      ...(taskMapRef.current.get(id) || {}),
      ...patch,
    });
    setTasks((prevTasks) =>
      prevTasks.map((task) => (task.id === id ? { ...task, ...patch } : task))
    );
  }, []);

  const completeUpload = useCallback(async (task, fileUuid) => {
    const result = await completeOpenResourceFileUpload({
      resourceUuid: task.resourceUuid,
      fileUuid,
      fileRelativePath: getUploadRelativePath(task),
    });
    return result?.data;
  }, []);

  const createAbortController = useCallback((id) => {
    const controller = new AbortController();
    abortControllerMapRef.current.set(id, controller);
    return controller;
  }, []);

  const stopActiveRequest = useCallback((id) => {
    const controller = abortControllerMapRef.current.get(id);
    if (controller) {
      controller.abort();
      abortControllerMapRef.current.delete(id);
    }
  }, []);

  const ensureTaskCanContinue = useCallback((id) => {
    const currentTask = taskMapRef.current.get(id);
    if (!currentTask || isStoppedStatus(currentTask.status)) {
      throw new Error(currentTask?.status || "canceled");
    }
    return currentTask;
  }, []);

  const uploadSingleFile = useCallback(
    async (task) => {
      const contentType = getContentType(task.file);
      const initResult = await initOpenResourceFileUpload({
        resourceUuid: task.resourceUuid,
        fileRelativePath: getUploadRelativePath(task),
        fileName: task.fileName,
        fileType: RESOURCE_FILE_TYPE,
        fileSize: task.size,
        contentType,
        totalChunks: 1,
      });

      const {
        fileUuid,
        presignedUrl,
        contentType: responseContentType,
        uploadStatus,
      } =
        initResult.data || {};
      if (!fileUuid || (!presignedUrl && uploadStatus !== "COMPLETED")) {
        throw new Error("init single upload response is invalid");
      }

      patchTask(task.id, { fileUuid, key: fileUuid });

      if (uploadStatus === "COMPLETED") {
        const completedFile = await completeUpload(task, fileUuid);
        ensureTaskCanContinue(task.id);
        patchTask(task.id, { ...(completedFile || {}), percent: 100 });
        return;
      }

      ensureTaskCanContinue(task.id);
      const controller = createAbortController(task.id);
      await putWithRetry(presignedUrl, task.file, {
        headers: {
          "Content-Type": responseContentType || contentType,
        },
        signal: controller.signal,
        onUploadProgress: (progressEvent) => {
          if (!progressEvent.total) {
            return;
          }
          if (isStoppedStatus(taskMapRef.current.get(task.id)?.status)) {
            return;
          }
          patchTask(task.id, {
            percent: Math.min(
              99,
              Math.round((progressEvent.loaded * 100) / progressEvent.total)
            ),
          });
        },
      });
      abortControllerMapRef.current.delete(task.id);

      ensureTaskCanContinue(task.id);
      const completedFile = await completeUpload(task, fileUuid);
      ensureTaskCanContinue(task.id);
      patchTask(task.id, { ...(completedFile || {}), percent: 100 });
    },
    [completeUpload, createAbortController, ensureTaskCanContinue, patchTask]
  );

  const uploadMultipartChunk = useCallback(
    async (task, part, chunks, totalSize, loadedMap) => {
      ensureTaskCanContinue(task.id);
      const chunk = chunks[part.chunkIndex];
      if (!chunk) {
        throw new Error(`chunk ${part.chunkIndex} is missing`);
      }

      const controller = createAbortController(`${task.id}-${part.chunkIndex}`);
      await putWithRetry(part.presignedUrl, chunk.data, {
        signal: controller.signal,
        onUploadProgress: (progressEvent) => {
          if (isStoppedStatus(taskMapRef.current.get(task.id)?.status)) {
            return;
          }
          loadedMap[part.chunkIndex] = progressEvent.loaded;
          const loadedSize = Object.values(loadedMap).reduce(
            (sum, loaded) => sum + loaded,
            0
          );
          patchTask(task.id, {
            percent: Math.min(99, Math.floor((loadedSize / totalSize) * 100)),
          });
        },
      });
      abortControllerMapRef.current.delete(`${task.id}-${part.chunkIndex}`);

      ensureTaskCanContinue(task.id);
      chunk.uploaded = true;
      loadedMap[part.chunkIndex] = chunk.data.size;
      const loadedSize = Object.values(loadedMap).reduce(
        (sum, loaded) => sum + loaded,
        0
      );
      patchTask(task.id, {
        percent: Math.min(99, Math.floor((loadedSize / totalSize) * 100)),
      });
    },
    [createAbortController, ensureTaskCanContinue, patchTask]
  );

  const syncMultipartProgress = useCallback(
    async (task, fileUuid, totalChunks) => {
      try {
        const result = await openResourceFileUploadProgress({
          resourceUuid: task.resourceUuid,
          fileUuid,
        });
        const uploadedChunks = result?.data?.uploadedChunks || 0;
        const serverTotalChunks = result?.data?.totalChunks || totalChunks;
        if (serverTotalChunks > 0) {
          patchTask(task.id, {
            percent: Math.min(
              99,
              Math.floor((uploadedChunks / serverTotalChunks) * 100)
            ),
          });
        }
      } catch (err) {
        console.log(err);
      }
    },
    [patchTask]
  );

  const uploadMultipartFile = useCallback(
    async (task) => {
      patchTask(task.id, { status: "hashing" });
      const chunkConfig = chunkSize(task.file.size);
      const CHUNK_SIZE = Math.max(
        chunkConfig.CHUNK_SIZE,
        MULTIPART_MIN_CHUNK_SIZE
      );
      const { fileHash, chunks, totalChunks } = await calculateHashes(
        task.file,
        CHUNK_SIZE
      );
      ensureTaskCanContinue(task.id);
      const chunkParams = chunks.map((item) => ({
        chunkIndex: item.index,
        chunkMd5: item.hash,
      }));

      patchTask(task.id, { status: "uploading" });
      const initResult = await initOpenResourceFileUpload({
        resourceUuid: task.resourceUuid,
        fileRelativePath: getUploadRelativePath(task),
        fileName: task.fileName,
        fileType: RESOURCE_FILE_TYPE,
        fileSize: task.size,
        contentType: getContentType(task.file),
        fileMd5: fileHash,
        totalChunks,
        chunkSize: CHUNK_SIZE,
        chunkParams,
      });

      const { fileUuid, parts = [], uploadStatus } = initResult.data || {};
      if (!fileUuid) {
        throw new Error("init multipart upload fileUuid is empty");
      }

      patchTask(task.id, { fileUuid, key: fileUuid });
      await syncMultipartProgress(task, fileUuid, totalChunks);
      ensureTaskCanContinue(task.id);

      if (uploadStatus === "COMPLETED") {
        const completedFile = await completeUpload(task, fileUuid);
        ensureTaskCanContinue(task.id);
        patchTask(task.id, { ...(completedFile || {}), percent: 100 });
        return;
      }

      const loadedMap = {};
      parts.forEach((part) => {
        const chunk = chunks[part.chunkIndex];
        if (part.uploaded && chunk) {
          chunk.uploaded = true;
          loadedMap[part.chunkIndex] = chunk.data.size;
        }
      });
      const uploadedSize = Object.values(loadedMap).reduce(
        (sum, loaded) => sum + loaded,
        0
      );
      if (uploadedSize > 0) {
        patchTask(task.id, {
          percent: Math.min(99, Math.floor((uploadedSize / task.size) * 100)),
        });
      }

      const pendingParts = parts.filter(
        (part) => !part.uploaded && part.presignedUrl
      );
      await Promise.all(
        pendingParts.map((part) =>
          partLimit(() =>
            uploadMultipartChunk(task, part, chunks, task.size, loadedMap)
          )
        )
      );

      ensureTaskCanContinue(task.id);
      const completedFile = await completeUpload(task, fileUuid);
      ensureTaskCanContinue(task.id);
      patchTask(task.id, { ...(completedFile || {}), percent: 100 });
    },
    [
      completeUpload,
      ensureTaskCanContinue,
      patchTask,
      syncMultipartProgress,
      uploadMultipartChunk,
    ]
  );

  const runUpload = useCallback(
    async (task) => {
      const currentTask = taskMapRef.current.get(task.id);
      if (!currentTask || isStoppedStatus(currentTask.status)) {
        return;
      }
      patchTask(task.id, { status: "uploading", error: "" });
      try {
        if (task.size <= SMALL_FILE_THRESHOLD) {
          await uploadSingleFile(task);
        } else {
          await uploadMultipartFile(task);
        }
        patchTask(task.id, { status: "done", percent: 100 });
        message.success("上传成功");
      } catch (err) {
        const latestTask = taskMapRef.current.get(task.id);
        if (
          latestTask?.status === "paused" ||
          latestTask?.status === "canceled" ||
          isCanceledError(err)
        ) {
          return;
        }
        console.log(err);
        patchTask(task.id, {
          status: "error",
          error: err?.message || "upload failed",
        });
        message.error("上传失败，可在头像下方进度栏重试");
      }
    },
    [patchTask, uploadMultipartFile, uploadSingleFile]
  );

  const enqueueUpload = useCallback(
    (resourceUuid, uploadFile) => {
      const id = getTaskId(resourceUuid, uploadFile.uid);
      if (taskMapRef.current.has(id)) {
        return id;
      }

      const task = {
        ...uploadFile,
        id,
        resourceUuid,
        percent: 0,
        status: "queued",
        createdAt: Date.now(),
      };

      taskMapRef.current.set(id, task);
      setTasks((prevTasks) => [...prevTasks, task]);
      fileLimit(() => runUpload(task));
      return id;
    },
    [runUpload]
  );

  const enqueueUploads = useCallback(
    (resourceUuid, uploadFiles) => {
      uploadFiles.forEach((uploadFile) => enqueueUpload(resourceUuid, uploadFile));
    },
    [enqueueUpload]
  );

  const retryUpload = useCallback(
    (id) => {
      const task = taskMapRef.current.get(id);
      if (!task || !task.file) {
        return;
      }
      patchTask(id, { percent: 0, status: "queued", error: "" });
      fileLimit(() => runUpload({ ...task, percent: 0, status: "queued" }));
    },
    [patchTask, runUpload]
  );

  const pauseUpload = useCallback(
    (id) => {
      const task = taskMapRef.current.get(id);
      if (!task || !task.file || !["queued", "hashing", "uploading"].includes(task.status)) {
        return;
      }
      patchTask(id, { status: "paused", error: "" });
      Array.from(abortControllerMapRef.current.keys())
        .filter((key) => key === id || key.startsWith(`${id}-`))
        .forEach(stopActiveRequest);
    },
    [patchTask, stopActiveRequest]
  );

  const resumeUpload = useCallback(
    (id) => {
      const task = taskMapRef.current.get(id);
      if (!task || !task.file || task.status !== "paused") {
        return;
      }
      patchTask(id, { status: "queued", error: "" });
      fileLimit(() => runUpload({ ...task, status: "queued" }));
    },
    [patchTask, runUpload]
  );

  const cancelUpload = useCallback(
    (id) => {
      const task = taskMapRef.current.get(id);
      if (!task || task.status === "done") {
        return;
      }
      patchTask(id, { status: "canceled", error: "" });
      Array.from(abortControllerMapRef.current.keys())
        .filter((key) => key === id || key.startsWith(`${id}-`))
        .forEach(stopActiveRequest);
    },
    [patchTask, stopActiveRequest]
  );

  const clearFinished = useCallback(() => {
    taskMapRef.current = new Map(
      Array.from(taskMapRef.current.entries()).filter(
        ([, task]) => task.status !== "done"
      )
    );
    setTasks((prevTasks) => prevTasks.filter((task) => task.status !== "done"));
  }, []);

  useEffect(() => {
    const canceledTasks = tasks.filter((task) => task.status === "canceled");
    if (!canceledTasks.length) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      const canceledIds = new Set(canceledTasks.map((task) => task.id));
      taskMapRef.current = new Map(
        Array.from(taskMapRef.current.entries()).filter(
          ([id]) => !canceledIds.has(id)
        )
      );
      setTasks((prevTasks) =>
        prevTasks.filter((task) => !canceledIds.has(task.id))
      );
    }, 3000);

    return () => window.clearTimeout(timer);
  }, [tasks]);

  const removeUploadTask = useCallback((resourceUuid, fileUuid) => {
    taskMapRef.current = new Map(
      Array.from(taskMapRef.current.entries()).filter(
        ([, task]) =>
          task.resourceUuid !== resourceUuid || task.fileUuid !== fileUuid
      )
    );
    setTasks((prevTasks) =>
      prevTasks.filter(
        (task) => task.resourceUuid !== resourceUuid || task.fileUuid !== fileUuid
      )
    );
  }, []);

  const value = useMemo(() => {
    const uploadTasks = tasks.filter(
      (task) => task.file && task.status !== "canceled"
    );
    const totalSize = uploadTasks.reduce((sum, task) => sum + task.size, 0);
    const uploadedSize = uploadTasks.reduce(
      (sum, task) => sum + (task.size * (task.percent || 0)) / 100,
      0
    );
    const totalPercent = totalSize
      ? Math.floor((uploadedSize / totalSize) * 100)
      : 100;
    const activeCount = uploadTasks.filter((task) =>
      ["queued", "hashing", "uploading"].includes(task.status)
    ).length;
    const pausedCount = uploadTasks.filter((task) => task.status === "paused").length;
    const errorCount = uploadTasks.filter((task) => task.status === "error").length;

    return {
      tasks,
      totalPercent,
      activeCount,
      pausedCount,
      errorCount,
      enqueueUpload,
      enqueueUploads,
      retryUpload,
      pauseUpload,
      resumeUpload,
      cancelUpload,
      clearFinished,
      removeUploadTask,
    };
  }, [
    cancelUpload,
    clearFinished,
    enqueueUpload,
    enqueueUploads,
    pauseUpload,
    removeUploadTask,
    resumeUpload,
    retryUpload,
    tasks,
  ]);

  return (
    <UploadQueueContext.Provider value={value}>
      {children}
    </UploadQueueContext.Provider>
  );
};

export const useUploadQueue = () => {
  const context = useContext(UploadQueueContext);
  if (!context) {
    throw new Error("useUploadQueue must be used within UploadQueueProvider");
  }
  return context;
};
